import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import HeroVisual from "@/components/HeroVisual";
import { VALUES } from "@/data/site";
import { ArrowRight, ShieldCheck, Zap, Cpu, TrendingUp, Users, Target, Calendar, Building2, Heart } from "lucide-react";

const valueIcons = [ShieldCheck, Users, Zap, Cpu, TrendingUp, Target];

export default function About() {
  return (
    <>
      <SEO title="Hakkımızda — Pratik Teknoloji" description="2015'te Çanakkale'de kurulan Pratik Bilişim Teknolojileri A.Ş. hakkında: vizyon, değerler ve marka tonu." />

      <PageHero
        badge="Hakkımızda"
        title={<>2015'te başladık.</>}
        titleAlt="Aynı heyecanla devam ediyoruz."
        rightSlot={
          <HeroVisual
            accentColor="#FF3B30"
            center={{ icon: <Heart size={36} strokeWidth={1.6} />, topLabel: "Markamız", bottomLabel: "Pratik" }}
            cards={[
              { position: "tr", rotate: 4, delay: 0.4, color: "#FF3B30", icon: <Calendar size={16} />, label: "Kuruluş", value: "2015", sub: "Çanakkale merkezli" },
              { position: "ml", rotate: -5, delay: 0.55, color: "#34C759", icon: <Building2 size={16} />, label: "Yapı", value: "A.Ş.", sub: "kurumsal şirket" },
              { position: "br", rotate: 3, delay: 0.7, color: "#007AFF", icon: <TrendingUp size={16} />, label: "Büyüme", value: "10+ yıl", sub: "sürekli gelişim" },
            ]}
          />
        }
      />

      {/* Body */}
      <section className="bg-[#0A0A0A] text-white relative">
        <div className="grain absolute inset-0 opacity-30 pointer-events-none"></div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pb-24">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7 space-y-6 text-lg text-white/70 leading-relaxed">
              <p><span className="text-white font-semibold">Pratik Teknoloji</span>, Haziran 2015'te Çanakkale'de şahıs şirketi olarak kurulduğunda tek bir hedefi vardı: işletmelerin teknolojiyle olan ilişkisini kolaylaştırmak, hızlandırmak ve güvenilir hâle getirmek.</p>
              <p>Kurulduğu ilk günden itibaren yazılım, donanım ve teknik servis alanlarında sahada aktif olarak çalışan şirket, kısa sürede bölgesinde güçlü bir çözüm ortağı hâline geldi.</p>
              <p>Genişleyen müşteri portföyü ve büyüyen hizmet ağı ile birlikte şirket, <span className="text-white font-semibold">19 Mart 2025</span> tarihinde <span className="text-white font-semibold">Pratik Bilişim Teknolojileri Anonim Şirketi</span> unvanıyla kurumsal bir yapıya dönüştü.</p>
              <p>Bugün Pratik Bilişim Teknolojileri A.Ş.; yazılım, donanım, güvenlik sistemleri ve teknik servis hizmetlerini tek merkezden sunan <span className="text-white font-semibold">yetkili teknoloji çözüm ortağıdır</span>.</p>
            </div>

            <aside className="lg:col-span-5">
              <div className="relative rounded-3xl bg-gradient-to-br from-[#1a1a1a] to-[#0A0A0A] border border-white/10 text-white p-10 overflow-hidden">
                <div className="absolute -top-20 -right-20 w-72 h-72 bg-[#FF3B30]/25 rounded-full blur-3xl"></div>
                <div className="relative">
                  <p className="font-display text-2xl md:text-3xl font-bold leading-snug">
                    "İşletmelerin teknolojiyle olan ilişkisini sadeleştiriyor, karmaşık süreçleri yönetilebilir kılıyor ve her ölçekte işletmeye güvenli, sürdürülebilir ve büyümeyi destekleyen bir dijital altyapı sunuyoruz."
                  </p>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="relative py-20 md:py-28 bg-[#0A0A0A] text-white border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-12 flex items-end justify-between gap-6 flex-wrap">
            <div>
              <h2 className="font-display text-4xl md:text-6xl font-bold tracking-tight">Gücümüzü değerlerimizden alıyoruz.</h2>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {VALUES.map((v, i) => {
              const Icon = valueIcons[i] || ShieldCheck;
              return (
                <div key={v.title} data-testid={`value-${i}`} className="rounded-2xl bg-white/[0.03] border border-white/10 p-7 hover:border-[#FF3B30]/50 hover:bg-white/[0.06] transition-all">
                  <div className="flex items-center justify-between mb-5">
                    <div className="h-11 w-11 rounded-xl flex items-center justify-center bg-[#FF3B30]/10 text-[#FF3B30] border border-[#FF3B30]/20">
                      <Icon size={20} strokeWidth={1.8} />
                    </div>
                    <div className="text-xs font-bold tracking-[0.25em] text-white/40">0{i + 1}</div>
                  </div>
                  <div className="font-display text-2xl font-bold text-white">{v.title}</div>
                  <p className="mt-3 text-white/60 leading-relaxed">{v.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Tone quote + CTA */}
      <section className="py-24 md:py-32 bg-[#0A0A0A] border-t border-white/10">
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
          <blockquote className="font-display text-3xl md:text-5xl font-bold leading-[1.1] tracking-tight text-white">
            "Kurumsal bir yapıya sahip olsak da, ilk günden beri taşıdığımız <span className="text-[#FF3B30]">amatör ruhu</span> ve çalışma heyecanını hiç kaybetmedik."
          </blockquote>
          <div className="mt-10">
            <Link to="/iletisim" className="inline-flex items-center gap-2 rounded-full bg-[#FF3B30] text-white px-7 py-4 font-semibold hover:bg-white hover:text-[#0A0A0A] transition-colors">
              Bizimle çalışmak ister misiniz? <ArrowRight size={18}/>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
